import { ThunkDispatch } from "@reduxjs/toolkit";
import JSZip from 'jszip';
import { Tooltip } from 'primereact/tooltip';
import React, { useRef, useEffect, memo, useState, useCallback } from 'react';
import { connect, useDispatch } from "react-redux";
import { toast } from 'react-toastify';

import IMAGE_EDITING_GUIDE from '../../../assets/images/pho_editing_guide.png';
import { readURIFromFile } from '../../libs';
import { imageEditingActions, ImageEditingState, UploadedImage, processImages } from '../../reducers/imageEditing';
import { serviceSettingsActions } from '../../reducers/serviceSettings';
import { toastActions } from '../../reducers/toast';
import { renderBlobToCanvasNoURL } from '../../utils/blobToCanvas';
import { LoadingIcon, DelPhotoIcon, AfterImageDownloadIcon, InfoIcon } from '../SVGIcons';
import IndividualPreviewPanel from './IndividualPreviewPanel';
import { HintPopup } from '../common/HintPopup';

// TypeScript型定義の拡張
declare module "react" {
  interface InputHTMLAttributes<T> extends React.HTMLAttributes<T> {
    webkitdirectory?: string;
    directory?: string;
  }
}

// 画像編集機能専用の最大アップロード数
const MAX_PHOTO_UPLOAD_IMAGE_EDITING = 50;

// State型定義
interface RootState {
  imageEditing: ImageEditingState;
  serviceSettings?: any;
  [key: string]: any;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const { selectedImage, processedImages } = state.imageEditing;

  // 選択中の画像に対応する処理結果を取得
  const currentProcessedImage = selectedImage && processedImages.length > 0
    ? processedImages.find(img => img.originalId === selectedImage.id) || null
    : null;

  return {
    uploadedImages: state.imageEditing.uploadedImages,
    selectedImageIndex: state.imageEditing.selectedImageIndex,
    selectedImage: state.imageEditing.selectedImage,
    isProcessing: state.imageEditing.isProcessing,
    processingProgress: state.imageEditing.processingProgress,
    serviceSettings: state.serviceSettings,
    features: state.imageEditing.features,
    processingMethod: state.imageEditing.processingMethod,
    resultPanelState: state.imageEditing.resultPanelState,
    currentProcessedImage,
    processedImages: state.imageEditing.processedImages,
    isLoading: state.imageEditing.isLoading,
    // v3追加: 個別処理モード用の状態
    editHistory: state.imageEditing.editHistory,
    currentHistoryIndex: state.imageEditing.currentHistoryIndex,
    showAfter: state.imageEditing.showAfter,
    isAIProcessing: state.imageEditing.isAIProcessing,
  };
}

interface ImageEditingMainAreaProps {
  uploadedImages: UploadedImage[];
  selectedImageIndex: number;
  selectedImage: UploadedImage | null;
  isProcessing: boolean;
  processingProgress: number;
  features: ImageEditingState['features'];
  processingMethod: 'single' | 'individual';
  resultPanelState: 'ng-examples' | 'processing' | 'results';
  currentProcessedImage: any;
  processedImages: any[];
  isLoading: boolean;
  // v3追加: 個別処理モード用
  editHistory: ImageEditingState['editHistory'];
  currentHistoryIndex: ImageEditingState['currentHistoryIndex'];
  showAfter: ImageEditingState['showAfter'];
  isAIProcessing: boolean;
}

const ImageEditingMainArea: React.FC<ImageEditingMainAreaProps> = (props) => {
  const { uploadedImages, selectedImageIndex, selectedImage, isProcessing, processingProgress, features, processingMethod, resultPanelState, currentProcessedImage, processedImages, isLoading, editHistory, currentHistoryIndex, showAfter, isAIProcessing } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const inputFile = useRef<HTMLInputElement | null>(null);
  const inputFolder = useRef<HTMLInputElement | null>(null);
  const [previousProcessedCount, setPreviousProcessedCount] = useState(0);
  const previousIsProcessingRef = useRef(false);
  const [showClearAllImagesHint, setShowClearAllImagesHint] = useState(false);
  const [dontShowClearAllImagesAgain, setDontShowClearAllImagesAgain] = useState(false);

  // 機能名を日本語に変換
  const getFeatureLabels = (features: string[]): string[] => {
    const featureMap: { [key: string]: string } = {
      watermark: 'ウォーターマーク',
      compression: '画像圧縮',
      mosaic: 'モザイク加工',
      highResolution: '高解像度処理',
      levelAdjustment: '水平調整',
      backgroundProcessing: '青空加工',
      contrastAdjustment: 'コントラスト',
      brightnessAdjustment: '明るさ',
      whiteBalance: 'ホワイトバランス'
    };
    // auto-compression-5mbは表示しない（内部的な自動圧縮のため）
    return features
      .filter(f => f !== 'auto-compression-5mb')
      .map(f => featureMap[f] || f);
  };

  // フォルダ選択用inputにwebkitdirectory属性を設定
  useEffect(() => {
    if (inputFolder.current) {
      inputFolder.current.setAttribute('webkitdirectory', '');
      inputFolder.current.setAttribute('directory', '');
    }
  }, []);

  // コンポーネントマウント時にServiceSettingsを取得
  useEffect(() => {
    dispatch(serviceSettingsActions.fetchServiceSettings());
  }, [dispatch]);

  // ServiceSettingsが更新されたらウォーターマーク設定を同期
  useEffect(() => {
    const watermarkSettings = props.serviceSettings;
    if (watermarkSettings && watermarkSettings.watermark_type && watermarkSettings.watermark_type !== 'none') {
      let settings = {};
      if (watermarkSettings.watermark_type === 'corporate_logo' && watermarkSettings.corporate_logo_file_data) {
        settings = {
          type: 'image',
          imageData: watermarkSettings.corporate_logo_file_data,
          opacity: 1 - ((watermarkSettings.corporate_logo_transparency || 0) / 100),
          size: watermarkSettings.corporate_logo_scale || 20,
          position: 'center'
        };
      } else if (watermarkSettings.watermark_type === 'individual_input' && watermarkSettings.individual_input_text) {
        settings = {
          type: 'text',
          text: watermarkSettings.individual_input_text,
          color: watermarkSettings.individual_input_color || '#FFFFFF',
          opacity: 1 - ((watermarkSettings.individual_input_transparency || 0) / 100),
          size: watermarkSettings.individual_input_scale || 10,
          position: 'center'
        };
      }

      // Redux stateのウォーターマーク設定を更新
      dispatch(imageEditingActions.updateFeatureSettings({
        featureName: 'watermark',
        settings
      }));
    }
  }, [props.serviceSettings, dispatch]);

  // 選択された画像のURLを自動生成
  useEffect(() => {
    const loadSelectedImageUrl = async () => {
      if (selectedImage && !selectedImage.url && selectedImage.file) {
        const url = await readURIFromFile(selectedImage.file) as string;
        dispatch(imageEditingActions.updateImageUrl({ id: selectedImage.id, url }));
      }
    };
    loadSelectedImageUrl();
  }, [selectedImage?.id]);

  // 処理完了時に自動ダウンロードを実行
  useEffect(() => {
    // 処理が完了した瞬間（isProcessingがtrueからfalseになった）を検知
    if (previousIsProcessingRef.current && !isProcessing) {
      // 処理が完了し、新しい画像がある場合
      if (processedImages.length > 0 &&
          processedImages.length > previousProcessedCount &&
          processingMethod === 'single') {
        // 少し遅延を入れて処理完了を視覚的に確認できるようにする
        const timer = setTimeout(() => {
          if (processedImages.length === 1) {
            // 1枚の場合は通常のダウンロード
            handleSingleDownload(processedImages[0]);
          } else {
            // 複数枚の場合はZIPダウンロード
            handleBatchDownload();
          }
          setPreviousProcessedCount(processedImages.length);
        }, 500);

        return () => clearTimeout(timer);
      }
    }

    // 現在のisProcessing状態を保存
    previousIsProcessingRef.current = isProcessing;
  }, [processedImages.length, isProcessing, processingMethod, previousProcessedCount]);

  // 画像全削除ヒントのlocalStorageチェック
  useEffect(() => {
    const hidden = localStorage.getItem('allImagesClearHintHidden');
    if (hidden === 'true' && showClearAllImagesHint) {
      setShowClearAllImagesHint(false);
    }
  }, [showClearAllImagesHint]);

  const onButtonAddImage = () => {
    if (inputFile.current) {
      inputFile.current.click();
    }
  };

  const onButtonAddFolder = () => {
    if (inputFolder.current) {
      inputFolder.current.click();
    }
  };

  const addImages = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      dispatch(imageEditingActions.addImages(e.target.files));
      // Reset the input value to allow selecting the same files again
      e.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add('bg-orange-50');
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-orange-50');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('bg-orange-50');

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      dispatch(imageEditingActions.addImages(e.dataTransfer.files));
    }
  };

  const handleImageSelect = async (index: number) => {
    dispatch(imageEditingActions.selectImage(index));

    // 選択された画像のURLが未生成の場合は生成
    const image = uploadedImages[index];
    if (image && !image.url && image.file) {
      const url = await readURIFromFile(image.file) as string;
      dispatch(imageEditingActions.updateImageUrl({ id: image.id, url }));
    }
  };

  const handleImageRemove = (imageId: string) => {
    dispatch(imageEditingActions.removeImage(imageId));
  };

  const handleExecute = (executeAll: boolean = false) => {

    const enabledFeatures = Object.entries(features)
      .filter(([_, feature]) => feature.enabled)
      .reduce((acc, [key, feature]) => {
        acc[key] = feature.settings || {};
        return acc;
      }, {} as any);

    const imageIds = processingMethod === 'single'
      ? uploadedImages.map(img => img.id)  // 一括処理：全画像
      : executeAll
        ? uploadedImages.map(img => img.id)  // 個別処理の「まとめて実行」：全画像
        : selectedImage ? [selectedImage.id] : [];  // 個別処理の「1枚ずつ実行」：選択画像のみ


    dispatch(processImages({
      imageIds,
      processingMethod,
      features: enabledFeatures,
    }));
  };

  const handleNewProject = () => {
    dispatch(imageEditingActions.clearData());
    setPreviousProcessedCount(0);
  };

  // 画像全削除のハンドラー
  const handleCloseClearAllImagesHint = () => {
    if (dontShowClearAllImagesAgain) {
      localStorage.setItem('allImagesClearHintHidden', 'true');
    }
    setShowClearAllImagesHint(false);
    setDontShowClearAllImagesAgain(false);
  };

  const handleExecuteClearAllImages = () => {
    handleCloseClearAllImagesHint();
    dispatch(imageEditingActions.clearUploadedImages());
    dispatch(toastActions.toastSuccess('全ての画像を削除しました'));
  };

  const handleClearAllImages = () => {
    const hidden = localStorage.getItem('allImagesClearHintHidden');
    if (hidden === 'true') {
      // 直接削除実行
      dispatch(imageEditingActions.clearUploadedImages());
      dispatch(toastActions.toastSuccess('全ての画像を削除しました'));
    } else {
      // ヒントポップアップを表示
      setShowClearAllImagesHint(true);
    }
  };

  const canExecute = uploadedImages.length > 0 && !isProcessing && Object.values(features).some(f => f.enabled);

  // 単一画像のダウンロード処理
  const handleSingleDownload = (image: any) => {
    if (image.blob) {
      const url = URL.createObjectURL(image.blob);
      const a = document.createElement('a');
      a.href = url;

      // 元の画像を探してファイル名を生成
      const originalImage = uploadedImages.find(img => img.id === image.originalId);
      let fileName = 'processed_image.jpg';

      if (originalImage && originalImage.name) {
        // 拡張子を除いたファイル名を取得
        const baseName = originalImage.name.replace(/\.[^/.]+$/, '');
        // 現在の時刻を追加
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:\-T]/g, '');
        fileName = `${baseName}_${timestamp}.jpg`;
      }

      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      dispatch(toastActions.toastSuccess('画像のダウンロードを開始しました'));
    }
  };

  // 一括ダウンロード処理
  const handleBatchDownload = async () => {
    if (processedImages.length === 0) return;

    try {
      const zip = new JSZip();

      // 現在の時刻を取得（ファイル名用）
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/[:\-T]/g, '');

      // 各画像をZIPに追加
      for (const image of processedImages) {
        if (image.blob) {
          // 元の画像を探す
          const originalImage = uploadedImages.find(img => img.id === image.originalId);
          let baseName = 'image';

          if (originalImage && originalImage.name) {
            // 拡張子を除いたファイル名を取得
            baseName = originalImage.name.replace(/\.[^/.]+$/, '');
          }

          // ファイル名を生成（元のファイル名_時刻.jpg）
          const fileName = `${baseName}_${timestamp}.jpg`;
          zip.file(fileName, image.blob);
        }
      }

      // ZIPファイルを非同期で生成（プログレス付き）
      const content = await zip.generateAsync(
        {
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: {
            level: 6 // 圧縮レベル（1-9、6がデフォルト）
          }
        },
        (metadata) => {
          // プログレスを表示（0-100%）
          const progress = Math.round(metadata.percent);
          if (progress % 10 === 0) { // 10%ごとに更新
          }
        }
      );

      // ダウンロード処理（フォルダ名も時刻ベース）
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${timestamp}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      dispatch(toastActions.toastSuccess('ZIPファイルのダウンロードを開始しました'));
    } catch (error) {
      dispatch(toastActions.toastError('ZIPファイルの作成に失敗しました'));
    }
  };

  // Canvas要素で直接描画する方式（最適化版）
  const ProcessedImageDisplay = memo(({ image, uploadedImages }: { image: any; uploadedImages: UploadedImage[] }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
      if (canvasRef.current && containerRef.current && image.blob) {
        setIsLoading(true);

        // 親要素のサイズを取得（p-3のパディングを考慮）
        const {parentElement} = containerRef.current;
        const containerWidth = parentElement ? parentElement.clientWidth - 24 : 800; // 24px = p-3 * 2
        const containerHeight = parentElement ? parentElement.clientHeight - 24 : 600;

        // CSP対応のCanvas描画を使用（高品質設定）
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // デバイスピクセル比を考慮して高解像度レンダリング
          const dpr = window.devicePixelRatio || 1;

          // 十分大きなサイズを指定して、元画像のサイズを維持
          const largeSize = 3000; // 十分大きな値

          renderBlobToCanvasNoURL(image.blob, canvas, {
            maxWidth: largeSize,
            maxHeight: largeSize
          }).then(() => {
            // 実際の画像サイズを取得
            const actualWidth = canvas.width;
            const actualHeight = canvas.height;
            // 高解像度キャンバスを作成
            const hdCanvas = document.createElement('canvas');
            hdCanvas.width = actualWidth * dpr;
            hdCanvas.height = actualHeight * dpr;
            const hdCtx = hdCanvas.getContext('2d');
            if (hdCtx) {
              // 高解像度で描画
              hdCtx.scale(dpr, dpr);
              hdCtx.drawImage(canvas, 0, 0);
              // 元のキャンバスに高解像度データをコピー
              canvas.width = hdCanvas.width;
              canvas.height = hdCanvas.height;
              ctx.drawImage(hdCanvas, 0, 0);
            }

            // 表示サイズを計算（コンテナに収まるように調整）
            let displayWidth = actualWidth;
            let displayHeight = actualHeight;

            if (displayWidth > containerWidth || displayHeight > containerHeight) {
              const scale = Math.min(
                containerWidth / displayWidth,
                containerHeight / displayHeight
              );
              displayWidth *= scale;
              displayHeight *= scale;
            }
            canvas.style.width = `${displayWidth}px`;
            canvas.style.height = `${displayHeight}px`;
            setDimensions({ width: displayWidth, height: displayHeight });
            setIsLoading(false);
          }).catch(error => {
            setIsLoading(false);
          });
        }
      }
    }, [image.blob]);

    // ウィンドウリサイズ時に再描画
    useEffect(() => {
      const handleResize = () => {
        if (canvasRef.current && containerRef.current && image.blob && !isLoading) {
          const {parentElement} = containerRef.current;
          const containerWidth = parentElement ? parentElement.clientWidth - 24 : 800;
          const containerHeight = parentElement ? parentElement.clientHeight - 24 : 600;

          const canvas = canvasRef.current;
          const dpr = window.devicePixelRatio || 1;

          // 十分大きなサイズを指定
          const largeSize = 3000;

          renderBlobToCanvasNoURL(image.blob, canvas, {
            maxWidth: largeSize,
            maxHeight: largeSize
          }).then(() => {
            // 実際の画像サイズ
            const actualWidth = canvas.width;
            const actualHeight = canvas.height;

            // 表示サイズを計算（コンテナに収まるように調整）
            let displayWidth = actualWidth;
            let displayHeight = actualHeight;

            if (displayWidth > containerWidth || displayHeight > containerHeight) {
              const scale = Math.min(
                containerWidth / displayWidth,
                containerHeight / displayHeight
              );
              displayWidth *= scale;
              displayHeight *= scale;
            }
            canvas.style.width = `${displayWidth}px`;
            canvas.style.height = `${displayHeight}px`;
            setDimensions({ width: displayWidth, height: displayHeight });
          });
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [image.blob, isLoading]);

    const handleDownload = () => {
      if (image.blob) {
        const url = URL.createObjectURL(image.blob);
        const a = document.createElement('a');
        a.href = url;

        // 元の画像を探してファイル名を生成
        const originalImage = uploadedImages.find(img => img.id === image.originalId);
        let fileName = 'processed_image.jpg';

        if (originalImage && originalImage.name) {
          // 拡張子を除いたファイル名を取得
          const baseName = originalImage.name.replace(/\.[^/.]+$/, '');
          // 現在の時刻を追加
          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:\-T]/g, '');
          fileName = `${baseName}_${timestamp}.jpg`;
        }

        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      }
    };

    return (
      <div ref={containerRef} className="w-full h-full flex items-center justify-center p-3">
        <div className="relative group max-w-full max-h-full">
          <div className="duration-100">
            <div className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 z-30 flex items-center">
              <button
                onClick={handleDownload}
                className="w-[72px] h-[72px] rounded-full bg-white/95 hover:bg-white/90 hidden group-hover:flex flex-col justify-center items-center z-30"
                disabled={isLoading}
                type="button"
                title="画像をダウンロード"
              >
                <AfterImageDownloadIcon className="w-6 h-6 mb-1" />
                <p className="text-sm text-center">保存</p>
              </button>
            </div>
            <span className="absolute group-hover:inset-0 group-hover:bg-gray-500/60 z-10 rounded-lg" />
            {isLoading && (
              <div className="flex justify-center items-center h-32">
                <span className="text-gray-500">画像を読み込み中...</span>
              </div>
            )}
            <canvas
              ref={canvasRef}
              className={`rounded-lg shadow-lg ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity block max-w-full max-h-full`}
              style={{
                imageRendering: 'high-quality',
                display: isLoading ? 'none' : 'block',
                objectFit: 'contain'
              }}
            />
          </div>
        </div>
      </div>
    );
  });

  const renderResultPanel = useCallback(() => {
    if (resultPanelState === 'ng-examples' || uploadedImages.length === 0) {
      return (
        <div className="h-full w-full flex flex-col">
          <div className="flex-1 overflow-hidden">
            <div className="h-full w-full bg-white rounded p-4">
              <img
                src={IMAGE_EDITING_GUIDE}
                alt="画像編集サンプル結果"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      );
    }

    if (resultPanelState === 'processing') {
      return (
        <div className="h-full w-full flex flex-col">
          {/* ヘッダースペース - 統一感のため */}
          <div className="flex-shrink-0 h-12 px-3 py-2 bg-white  flex justify-between items-center">
            <span className="text-sm text-gray-600 font-medium" />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
            <div className="relative w-32 h-32 mb-4">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
                <circle
                  cx="18"
                  cy="18"
                  r="16"
                  fill="none"
                  className="stroke-current text-gray-200"
                  strokeWidth="2"
                />
                <circle
                  cx="18"
                  cy="18"
                  r="16"
                  fill="none"
                  className="stroke-current text-blue-600"
                  strokeWidth="2"
                  strokeDasharray="100"
                  strokeDashoffset={100 - processingProgress}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">{processingProgress}%</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">処理中...</p>
            <div className="w-full max-w-xs bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
          </div>
        </div>
      );
    }

    if (resultPanelState === 'results' && currentProcessedImage) {
      return (
        <div className="h-full w-full flex flex-col">
          {/* 詳細情報のNoticeIcon - 上部に配置 */}
          <div className="flex-shrink-0 h-12 px-3 py-2 bg-white  flex items-center">
            <div className="flex items-center">
              <Tooltip target=".result-info-notice" className="max-w-xs" />
              <div
                className="result-info-notice bg-gray-100 rounded-full p-1.5 cursor-pointer hover:bg-gray-200 transition-colors"
                data-pr-tooltip={`ファイル名: ${uploadedImages.find(img => img.id === currentProcessedImage.originalId)?.name || 'unknown'}\n処理時間: ${(currentProcessedImage.processingTime / 1000).toFixed(1)}秒\nファイルサイズ: ${(currentProcessedImage.originalSize / 1024 / 1024).toFixed(2)}MB → ${(currentProcessedImage.fileSize / 1024 / 1024).toFixed(2)}MB\n適用された機能: ${getFeatureLabels(currentProcessedImage.appliedFeatures).join(', ')}\n処理日時: ${new Date().toLocaleString('ja-JP')}`}
                data-pr-position="right"
              >
                <InfoIcon className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </div>
          {/* 画像表示エリア */}
          <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
            <ProcessedImageDisplay image={currentProcessedImage} uploadedImages={uploadedImages} />
          </div>
        </div>
      );
    }

    return null;
  }, [resultPanelState, uploadedImages.length, currentProcessedImage, processingProgress]);

  // 結果パネルをメモ化されたコンポーネントとして定義
  const ResultPanel = memo(({ resultPanelState, uploadedImages, currentProcessedImage, processingProgress, renderResultPanel }: any) => {
    return <>{renderResultPanel()}</>;
  });

  return (
    <div className="bg-white border border-gray-300 rounded-lg flex flex-col overflow-hidden min-w-0 h-full" style={processingMethod === 'individual' ? {} : { minHeight: '600px' }}>
      <style>{`
        .result-info-notice .p-tooltip-text {
          white-space: pre-line;
        }
      `}</style>
      {/* サムネイルエリア - 全幅 */}
      <div className="h-[80px] border-b border-gray-300 bg-white flex flex-col min-w-0">
        <div className="flex items-center justify-between px-2 py-1 bg-gray-50  flex-shrink-0">
          <span className="text-xs text-gray-600 font-medium">
            {isLoading ? (
              "アップロード中..."
            ) : (
              <>
                アップロード済み: {uploadedImages.length} / {processingMethod === 'individual' ? 50 : MAX_PHOTO_UPLOAD_IMAGE_EDITING}枚
                {uploadedImages.length >= (processingMethod === 'individual' ? 50 : MAX_PHOTO_UPLOAD_IMAGE_EDITING) && (
                  <span className="text-orange-600">（上限に達しました）</span>
                )}
              </>
            )}
          </span>
          {/* クリアボタン */}
          {uploadedImages.length > 0 && (
            <button
              type="button"
              onClick={handleClearAllImages}
              disabled={isProcessing || isLoading}
              className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                isProcessing || isLoading
                  ? 'text-gray-500 bg-gray-300 cursor-not-allowed'
                  : 'bg-gray-400 text-white hover:bg-gray-500'
              }`}
            >
              クリア
            </button>
          )}
        </div>
        <div
          className="flex-1 overflow-x-auto p-1 overflow-y-hidden"
          style={{ paddingLeft: '12px', paddingTop: '4px', paddingBottom: '4px', paddingRight: '4px' }}
        >
          <div className="inline-flex gap-2 items-center" style={{ height: '60px' }}>
            {/* 画像追加ボタン - 一番左に配置 */}
            {uploadedImages.length < (processingMethod === 'individual' ? 50 : MAX_PHOTO_UPLOAD_IMAGE_EDITING) && (
              <div className="flex gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={onButtonAddImage}
                  className="border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-gray-400"
                  style={{ width: '42px', height: '60px' }}
                  title="画像を選択"
                  disabled={isProcessing || isLoading}
                >
                  <svg className="w-6 h-6 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7 7V5" />
                  </svg>
                </button>
              </div>
            )}

            {/* サムネイル画像 */}
            {uploadedImages.map((image, index) => (
              <div
                key={image.id}
                className={`relative flex-shrink-0 w-[84px] h-[60px] rounded-md cursor-pointer overflow-hidden ${
                  index === selectedImageIndex
                    ? 'ring-offset-2 ring-2 ring-blue-500'
                    : 'border-2 border-gray-300'
                }`}
                onClick={() => handleImageSelect(index)}
              >
                <img
                  src={image.thumbnail}
                  alt={image.name}
                  className="w-[84px] h-[60px] object-cover"
                  style={{ width: '84px', height: '60px' }}
                />
                {!isProcessing && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageRemove(image.id);
                    }}
                    className="absolute top-1 right-1 bg-white rounded-full p-0.5 opacity-80 hover:opacity-100"
                    title="画像を削除"
                  >
                    <DelPhotoIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* メインコンテンツエリア - 処理モードに応じて切り替え */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {processingMethod === 'single' ? (
          /* 一括処理モード：既存の2分割レイアウト */
          <>
            {/* 左側：元画像とその実行ボタン */}
            <div className="flex-1 min-h-0 border-r border-gray-300 flex flex-col">
          {uploadedImages.length > 0 && (
            /* ヘッダースペース - 画像がある場合のみ表示 */
            <div className="flex-shrink-0 h-12 px-3 py-2 bg-white ">
              <div className="h-full flex items-center justify-center">
                {/* <span className="text-sm text-gray-600 font-medium truncate max-w-full">
                  {selectedImage ? selectedImage.name : '元画像'}
                </span> */}
              </div>
            </div>
          )}
          <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden" >
            {uploadedImages.length > 0 ? (
              selectedImage ? (
                <div className="h-full flex items-center justify-center p-3">
                  {selectedImage.url ? (
                    <img
                      src={selectedImage.url}
                      alt={selectedImage.name}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm">画像が選択されていません</p>
                  </div>
                </div>
              )
            ) : (
              <div
                className="h-full flex items-center justify-center p-3"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className="flex flex-col items-center justify-center w-full h-full max-w-lg border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 space-y-3">
                    <svg className="w-12 h-12 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-2 text-sm">
                      <span className="font-semibold text-blue-600">画像を選択、またはドラッグ＆ドロップ</span>
                    </p>
                    <p className="text-sm">
                      <span className="font-semibold text-blue-600">最大{processingMethod === 'individual' ? 50 : 50}枚まで入力可能です</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      image/jpeg, image/png, image/webpが使用可能（1枚あたり10MBまで）
                    </p>
                    <div className="flex gap-2">
                      {/* フォルダ選択ボタン - 非表示 */}
                      {false && (
                        <button
                          type="button"
                          onClick={onButtonAddFolder}
                          className="px-3 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                          フォルダを選択
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={onButtonAddImage}
                        className="px-3 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        画像を選択
                      </button>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
          {/* 実行ボタン */}
          <div className="flex-shrink-0 border-t border-gray-300 py-3">
            <div className="flex justify-center px-4">
              <div className="relative" style={{ width: '500px', maxWidth: '90%' }}>
                {processingMethod === 'single' ? (
                  // 一括処理：従来通り1つのボタン
                  <button
                    type="button"
                    onClick={() => handleExecute()}
                    disabled={!canExecute}
                    className={
                      canExecute || isProcessing
                        ? "w-full px-5 py-2.5 rounded-full text-sm text-white submit-btn hover:submit-btn h-10 disabled:opacity-75"
                        : "w-full px-5 py-2.5 rounded-full text-sm text-gray-900 bg-gray-400 disabled:opacity-75 h-10"
                    }
                  >
                    <span className="flex items-center justify-center">
                      {isProcessing && (
                        <LoadingIcon className="inline-block w-4 h-4 mr-1 animate-spin" />
                      )}
                      <span>実行する</span>
                    </span>
                  </button>
                ) : (
                  // 個別処理：2つのボタン
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleExecute(true)}
                      disabled={!canExecute}
                      className={
                        canExecute || isProcessing
                          ? "flex-1 px-5 py-2.5 rounded-full text-sm text-white submit-btn hover:submit-btn h-10 disabled:opacity-75"
                          : "flex-1 px-5 py-2.5 rounded-full text-sm text-gray-900 bg-gray-400 disabled:opacity-75 h-10"
                      }
                    >
                      <span className="flex items-center justify-center">
                        {isProcessing && (
                          <LoadingIcon className="inline-block w-4 h-4 mr-1 animate-spin" />
                        )}
                        <span>まとめて実行</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExecute(false)}
                      disabled={!canExecute}
                      className={
                        canExecute || isProcessing
                          ? "flex-1 px-5 py-2.5 rounded-full text-sm text-white submit-btn hover:submit-btn h-10 disabled:opacity-75"
                          : "flex-1 px-5 py-2.5 rounded-full text-sm text-gray-900 bg-gray-400 disabled:opacity-75 h-10"
                      }
                    >
                      <span className="flex items-center justify-center">
                        {isProcessing && (
                          <LoadingIcon className="inline-block w-4 h-4 mr-1 animate-spin" />
                        )}
                        <span>1枚ずつ実行</span>
                      </span>
                    </button>
                  </div>
                )}
                {/* 無料バッジ（個別処理モードでは非表示） */}
                {processingMethod !== 'individual' && (
                  <div
                    className="absolute"
                    style={{
                    top: '-24px',
                    right: '-8px',
                    transform: 'rotate(12deg)',
                    zIndex: 10,
                    width: '56px',
                    height: '46px'
                  }}
                >
                  <div className="relative" style={{ width: '56px', height: '46px' }}>
                    <svg width="56" height="46" viewBox="0 0 56 46" style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))', display: 'block' }}>
                      {/* ギザギザの爆発形状の背景 - 14角形 */}
                      <path
                        d="M28 4 L32 8 L38 7 L39 12 L44 14 L43 19 L48 21 L45 25 L48 29 L43 32 L44 37 L39 39 L38 43 L32 42 L28 46 L24 42 L18 43 L17 39 L12 37 L13 32 L8 29 L11 25 L8 21 L13 19 L12 14 L17 12 L18 7 L24 8 Z"
                        fill="#ffe8cc"
                        stroke="#ff4500"
                        strokeWidth="2"
                      />
                    </svg>
                    <div
                      className="absolute flex items-center justify-center"
                      style={{
                        top: '52%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <span className="text-gray-600 font-bold text-xs" style={{ letterSpacing: '0.05em' }}>無料</span>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 右側：結果表示とその新規作成ボタン */}
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 relative overflow-hidden" >
            <ResultPanel
              resultPanelState={resultPanelState}
              uploadedImages={uploadedImages}
              currentProcessedImage={currentProcessedImage}
              processingProgress={processingProgress}
              renderResultPanel={renderResultPanel}
            />
          </div>
          {/* 一括ダウンロードボタンと新規作成ボタン */}
          <div className="flex-shrink-0 border-t border-gray-300 py-3">
            <div className="flex justify-center gap-3 px-4">
              {processedImages.length > 1 && (
                <button
                  type="button"
                  onClick={handleBatchDownload}
                  className="px-5 py-2.5 rounded-full text-sm text-white submit-btn-2nd hover:bg-gray-100 h-10"
                  style={{ width: '240px' }}
                >
                  一括ダウンロード ({processedImages.length}枚)
                </button>
              )}
              <button
                type="button"
                onClick={handleNewProject}
                className={
                  isProcessing || uploadedImages.length === 0
                    ? "px-5 py-2.5 rounded-full text-sm text-gray-900 bg-gray-400 disabled:opacity-75 h-10"
                    : "px-5 py-2.5 rounded-full text-sm text-white submit-btn-2nd hover:bg-gray-100 h-10"
                }
                style={{ width: processedImages.length > 1 ? '240px' : '500px', maxWidth: '90%' }}
                disabled={isProcessing || uploadedImages.length === 0}
              >
                新規作成
              </button>
            </div>
          </div>
        </div>
          </>
        ) : (
          /* 個別処理モード：画像があればIndividualPreviewPanel、なければドラッグ&ドロップエリア */
          <div className="flex-1 min-h-0 relative overflow-hidden">
            {uploadedImages.length > 0 ? (
              <IndividualPreviewPanel />
            ) : (
              <div
                className="w-full h-full flex items-start justify-center"
                style={{ padding: '24px' }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className="flex flex-col items-center justify-center border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors" style={{ width: '80%', maxWidth: '700px', aspectRatio: '16/9' }}>
                  <div className="flex flex-col items-center justify-center pt-4 pb-4 space-y-1.5">
                    <svg className="w-12 h-12 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className="mb-1 text-sm">
                      <span className="font-semibold text-blue-600">画像を選択、またはドラッグ＆ドロップ</span>
                    </p>
                    <p className="text-sm">
                      <span className="font-semibold text-blue-600">最大{processingMethod === 'individual' ? 50 : 50}枚まで入力可能です</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      image/jpeg, image/png, image/webpが使用可能（1枚あたり10MBまで）
                    </p>
                    <div className="flex gap-2 mt-1">
                      <button
                        type="button"
                        onClick={onButtonAddImage}
                        className="px-3 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        画像を選択
                      </button>
                    </div>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Hidden file inputs for thumbnail area buttons */}
      <input
        ref={inputFile}
        type="file"
        className="hidden"
        multiple
        accept="image/png, image/jpeg, image/webp"
        onChange={addImages}
        aria-label="画像ファイルを選択"
      />
      <input
        ref={inputFolder}
        type="file"
        className="hidden"
        multiple
        onChange={addImages}
        aria-label="フォルダを選択"
        webkitdirectory=""
        directory=""
      />

      {/* 画像全削除確認ポップアップ */}
      <HintPopup
        show={showClearAllImagesHint}
        title="全ての画像を削除"
        description={
          <div className="space-y-3">
            <p>アップロード済みの全ての画像（{uploadedImages.length}枚）を削除します。</p>
            <p className="text-red-600 font-medium">
              この操作は取り消せません。
            </p>
            <p className="text-sm text-gray-600">
              ※ 加工機能設定やテンプレート設定は削除されません。
            </p>
          </div>
        }
        onClose={handleCloseClearAllImagesHint}
        showCheckbox={true}
        checkboxValue={dontShowClearAllImagesAgain}
        onCheckboxChange={setDontShowClearAllImagesAgain}
        showExecuteButton={true}
        onExecute={handleExecuteClearAllImages}
        executeButtonText="削除"
      />
    </div>
  );
};

export default connect(mapStateToProps)(ImageEditingMainArea);